import React, { useEffect } from 'react';

const sections = [
  {
    number: '1',
    title: 'Introduction',
    content: 'This Privacy Policy explains how SpotFinder Kenya Limited ("SpotFinder", "we", "us", or "our") collects, uses, shares, stores, and protects personal data when you use our parking technology platform, including the SpotFinder Customer mobile application, SpotFinder Manager application, SpotGenie on-site application, administrative web portals, and related services (together, the "Platform"). We process personal data in accordance with the Kenya Data Protection Act, 2019 ("DPA"), its regulations, and guidance issued by the Office of the Data Protection Commissioner ("ODPC").',
  },
  {
    number: '2',
    title: 'Who This Policy Applies To',
    bullets: [
      'Customers who search for, book, or pay for parking through the Platform.',
      'Parking facility operators ("Managers") who list parking spaces and receive settlements.',
      'On-site agents ("SpotGenies") who assist with walk-in parking and payments.',
      'Visitors who pay for walk-in parking via QR codes or public payment pages.',
      'Administrators and support staff who access controlled back-office tools.',
    ],
    footer: 'If you do not agree with this Policy, please do not use the Platform. Where we require your consent (for example marketing push notifications), we will ask separately and you may withdraw consent at any time.',
  },
  {
    number: '3',
    title: 'Data Controller and Contact',
    definitions: [
      { term: 'Data Controller', def: 'SpotFinder Kenya Limited' },
      { term: 'DPO Email', def: 'dpo@spotfinder.co.ke' },
      { term: 'Support Email', def: 'support@spotfinder.co.ke' },
      { term: 'Website', def: 'https://spotfinder.co.ke' },
      { term: 'Platform API', def: 'https://api.spotfinder.co.ke' },
      { term: 'Regulator', def: 'Office of the Data Protection Commissioner (ODPC) — https://www.odpc.go.ke' },
    ],
  },
  {
    number: '4',
    title: 'Personal Data We Collect',
    content: 'We collect only data reasonably necessary to operate the Platform. Categories include:',
    subsections: [
      {
        title: '4.1 Account and profile data',
        bullets: [
          'Name, email address, mobile phone number, and gender (where you provide it).',
          'Firebase authentication identifiers used to secure your account.',
          'Country of use (Kenya — country code KE).',
          'Profile photo or preferences you choose to upload.',
        ],
      },
      {
        title: '4.2 Booking and parking data',
        bullets: [
          'Vehicle registration / plate number, booking dates and times, parking location, slot assignment.',
          'Walk-in session details, entry/exit timestamps, and payment status.',
          'Optional images of parking facilities uploaded by Managers.',
        ],
      },
      {
        title: '4.3 Payment and wallet data',
        bullets: [
          'Transaction amounts, currency, payment method (M-Pesa, card, wallet, or other enabled methods).',
          'Payment references, receipt metadata, wallet balance movements, coupons applied, and tax lines.',
          'We do not store full card numbers on our servers; card data is handled by Paystack under PCI-DSS standards.',
        ],
      },
      {
        title: '4.4 Manager KYC and payout data (Managers only)',
        bullets: [
          'Government ID or business verification documents submitted for KYC review.',
          'Bank account or M-Pesa payout details for settlements.',
          'Audit records of approval, rejection, or restriction decisions.',
        ],
      },
      {
        title: '4.5 Device and technical data',
        bullets: [
          'Mobile device tokens for push notifications (Firebase Cloud Messaging).',
          'App version, device type, IP address, and diagnostic logs for security and fraud prevention.',
          'Approximate location when you use map features (with your device permission).',
        ],
      },
      {
        title: '4.6 Communications and support',
        bullets: [
          'Messages you send to support, feedback, ratings, and dispute records.',
          'Records of policy versions you accept (terms, privacy, refund, and cancellation policies).',
        ],
      },
      {
        title: '4.7 Automated gate and ALPR events (where enabled)',
        content: 'At parking sites that use automated number-plate recognition (ALPR) or gate controllers connected through our edge gateway, we may process plate reads, lane identifiers, and gate open/close events to validate bookings or walk-ins. Edge devices forward events to our cloud API; they do not replace the Parking Operator\'s physical security obligations.',
      },
    ],
  },
  {
    number: '5',
    title: 'How and Why We Use Personal Data',
    content: 'We use personal data for the purposes below. Under the DPA, we rely on one or more lawful bases such as performance of a contract, compliance with a legal obligation, your consent, or our legitimate interests (balanced against your rights).',
    table: [
      { purpose: 'Create and manage your account', data: 'Name, phone, email, Firebase ID', basis: 'Contract; consent at registration' },
      { purpose: 'Provide parking bookings, walk-ins, and slot allocation', data: 'Plate number, booking times, parking ID', basis: 'Contract' },
      { purpose: 'Process payments, wallets, refunds, and Manager settlements', data: 'Payment amounts, M-Pesa/Paystack references, payout profile', basis: 'Contract; legal obligation (tax/audit)' },
      { purpose: 'Verify Manager identity and prevent fraud', data: 'KYC documents, risk signals, audit logs', basis: 'Legal obligation; legitimate interest' },
      { purpose: 'Send service notifications', data: 'Device token, booking summary', basis: 'Contract; consent where required' },
      { purpose: 'Improve security and maintain audit trails', data: 'IP address, logs, policy acceptances', basis: 'Legitimate interest; legal obligation' },
      { purpose: 'Comply with law and respond to regulators', data: 'Any data required by valid legal process', basis: 'Legal obligation' },
    ],
    footer: 'We do not sell your personal data. We do not use your data for unrelated profiling that produces legal or similarly significant effects without appropriate safeguards and, where required, your consent.',
  },
  {
    number: '6',
    title: 'How We Share Personal Data',
    content: 'We share personal data only as described below:',
    subsections: [
      {
        title: '6.1 Parking Operators and SpotGenies',
        content: 'When you book or use parking at a facility, the relevant Manager and authorised SpotGenies receive the information they need to honour your booking (for example your name, plate number, and booking window). Managers are independent operators responsible for their facilities; their own privacy practices may apply on-site.',
      },
      {
        title: '6.2 Service providers (processors)',
        content: 'We use trusted third parties who process data on our instructions and under contractual safeguards:',
        processors: [
          { name: 'Amazon Web Services (AWS)', purpose: 'Cloud hosting, database, encrypted file storage', region: 'European Union (Ireland) / as configured' },
          { name: 'Google Firebase', purpose: 'Secure sign-in, identity verification, push notifications', region: 'Global (Google infrastructure)' },
          { name: 'Paystack', purpose: 'Card and mobile money payment processing', region: 'Kenya / Nigeria (Paystack infrastructure)' },
          { name: 'Mapbox / Google Maps', purpose: 'Maps, location search, and routing assistance', region: 'United States / Global' },
        ],
      },
      {
        title: '6.3 Legal and safety disclosures',
        bullets: [
          'Where required by Kenyan law, court order, or valid request from a regulator or law enforcement agency.',
          'To protect the rights, property, or safety of SpotFinder, users, or the public.',
          'In connection with a merger, acquisition, or asset sale, subject to confidentiality and continued protection.',
        ],
      },
    ],
  },
  {
    number: '7',
    title: 'International Data Transfers',
    content: 'SpotFinder uses cloud providers that may process personal data outside Kenya (for example in the European Union or United States). Where a transfer is not to a country recognised as providing adequate protection, we implement appropriate safeguards such as standard contractual clauses, processor due diligence, encryption in transit and at rest, and access controls. You may request further information about transfer safeguards by contacting the DPO.',
  },
  {
    number: '8',
    title: 'Data Retention',
    bullets: [
      'Active account data is kept while your account remains open and as needed to provide the Platform.',
      'After account deletion, identifiable data is anonymised or erased within 30 days where possible, except where longer retention is required by law.',
      'Payment and ledger records may be retained for statutory financial retention periods.',
      'KYC documents for Managers are retained in line with our KYC policy and legal requirements.',
      'Security and audit logs are retained for a limited period to investigate incidents and demonstrate compliance.',
    ],
  },
  {
    number: '9',
    title: 'Your Rights Under the DPA',
    content: 'Subject to the DPA and applicable exceptions, you have the right to:',
    bullets: [
      'Be informed of the use of your personal data (this Policy).',
      'Access personal data we hold about you.',
      'Request correction of inaccurate or incomplete data.',
      'Request deletion or anonymisation where there is no lawful reason to retain the data.',
      'Object to processing based on legitimate interests where your circumstances require it.',
      'Withdraw consent at any time for processing that relies on consent.',
      'Lodge a complaint with the ODPC at https://www.odpc.go.ke.',
    ],
    subsections: [
      {
        title: '9.1 How to exercise your rights',
        bullets: [
          'Email dpo@spotfinder.co.ke with your request and enough information to verify your identity.',
          'Use in-app account settings where available in the Customer, Manager, and SpotGenie apps.',
          'Data export: request a structured export via the Platform API (/api/me/privacy/export) from within the app.',
          'Account deletion: request erasure via the Platform (/api/me/privacy/delete). Deletion may be delayed if you have active bookings, unpaid amounts, open disputes, or legal holds.',
          'We respond to verified requests within thirty (30) days as prescribed by the DPA.',
        ],
      },
    ],
  },
  {
    number: '10',
    title: 'Security',
    bullets: [
      'TLS encryption for data in transit between apps and our API.',
      'Firebase Authentication for secure sign-in; we do not store your password in our database.',
      'Role-based access controls for administrative functions and audit logging of sensitive actions.',
      'Encrypted storage for sensitive credentials (for example gate/ALPR configuration).',
      'Webhook signature verification for payment providers and fraud/risk monitoring.',
      'Regular review of access rights and incident response procedures.',
    ],
    footer: 'No method of transmission or storage is completely secure. If we become aware of a personal data breach likely to pose a risk to you, we will notify you and the ODPC as required by law.',
  },
  {
    number: '11',
    title: 'Children',
    content: 'The Platform is not directed at children under 18 years. You must be at least 18 years old to create an account. If we learn that we have collected personal data from a child without appropriate authority, we will delete it promptly.',
  },
  {
    number: '12',
    title: 'Cookies and Similar Technologies',
    content: 'Our web portals may use essential cookies and local storage needed for login sessions and security. We do not use non-essential tracking cookies without notice and, where required, consent. Mobile apps use device identifiers and notification tokens as described above.',
  },
  {
    number: '13',
    title: 'Automated Decision-Making',
    content: 'We may use automated systems to detect fraud, duplicate accounts, or policy violations (for example risk scoring or account restrictions). These systems support human review; you may contact the DPO if you believe a restriction was applied in error.',
  },
  {
    number: '14',
    title: 'Changes to This Policy',
    content: 'We may update this Policy from time to time. Material changes will be notified through the Platform or by email where appropriate. The "Effective date" at the top will be updated, and you may be asked to accept a new version before continuing to use paid features.',
  },
  {
    number: '15',
    title: 'Governing Law',
    content: 'This Privacy Policy is governed by the laws of the Republic of Kenya, including the Data Protection Act, 2019.',
  },
];

export default function Privacy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Privacy Policy — Spot Finder';
  }, []);

  return (
    <main id="main-content">
      {/* Hero */}
      <section
        style={{
          background: 'linear-gradient(135deg, #1B2A4A 0%, #0D1117 100%)',
          padding: '80px 24px 60px',
          textAlign: 'center',
          color: '#fff',
        }}
      >
        <div style={{ maxWidth: 760, margin: '0 auto' }}>
          <span
            style={{
              display: 'inline-block',
              background: 'rgba(0,168,120,0.2)',
              border: '1px solid rgba(0,168,120,0.4)',
              borderRadius: 20,
              padding: '6px 18px',
              fontSize: '0.82rem',
              fontWeight: 600,
              letterSpacing: 1,
              textTransform: 'uppercase',
              marginBottom: 20,
              color: '#00A878',
            }}
          >
            Legal
          </span>
          <h1
            style={{
              fontWeight: 900,
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              letterSpacing: -1,
              marginBottom: 16,
            }}
          >
            Privacy Policy
          </h1>
          <p style={{ fontSize: '1.05rem', opacity: 0.9, marginBottom: 8 }}>
            Effective date: 23 June 2026 · Version 1.0
          </p>
          <p style={{ fontSize: '0.9rem', opacity: 0.6 }}>
            SpotFinder Kenya Limited · Kenya Data Protection Act, 2019
          </p>
        </div>
      </section>

      {/* Notice banner */}
      <div
        style={{
          background: '#FFF8E1',
          borderBottom: '1px solid #FFE082',
          padding: '14px 24px',
          textAlign: 'center',
          fontSize: '0.88rem',
          color: '#795548',
        }}
      >
        This document is a draft template prepared for review by qualified legal counsel in Kenya before publication.
      </div>

      {/* Body */}
      <section style={{ background: '#F5F5F5', padding: '60px 24px' }}>
        <div style={{ maxWidth: 820, margin: '0 auto' }}>

          {/* Intro card */}
          <div
            style={{
              background: '#fff',
              borderRadius: 16,
              padding: '32px 36px',
              marginBottom: 24,
              borderLeft: '4px solid #1B2A4A',
            }}
          >
            <p style={{ color: '#444', lineHeight: 1.8, margin: 0 }}>
              Your privacy matters to us. This Policy explains what personal data SpotFinder collects,
              why we collect it, how we use it, and your rights under Kenyan law. We process your data
              in accordance with the Kenya Data Protection Act, 2019.
            </p>
          </div>

          {/* Sections */}
          {sections.map((sec) => (
            <div
              key={sec.number}
              id={'section-' + sec.number}
              style={{
                background: '#fff',
                borderRadius: 16,
                padding: '32px 36px',
                marginBottom: 20,
              }}
            >
              {/* Header */}
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 18 }}>
                <span
                  style={{
                    background: '#EEF2FF',
                    color: '#1B2A4A',
                    fontWeight: 800,
                    fontSize: '0.85rem',
                    borderRadius: 8,
                    padding: '4px 12px',
                    whiteSpace: 'nowrap',
                    marginTop: 2,
                  }}
                >
                  {sec.number}
                </span>
                <h2 style={{ fontWeight: 800, fontSize: '1.2rem', color: '#0D1117', margin: 0 }}>
                  {sec.title}
                </h2>
              </div>

              {/* Content */}
              {sec.content && (
                <p style={{ color: '#444', lineHeight: 1.8, marginBottom: sec.bullets || sec.subsections || sec.definitions || sec.table ? 16 : 0 }}>
                  {sec.content}
                </p>
              )}

              {/* Definitions */}
              {sec.definitions && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {sec.definitions.map((d) => (
                    <div
                      key={d.term}
                      style={{
                        display: 'flex',
                        gap: 12,
                        padding: '12px 16px',
                        background: '#F8F9FF',
                        borderRadius: 10,
                        border: '1px solid #E8ECF8',
                      }}
                    >
                      <span style={{ fontWeight: 700, color: '#1B2A4A', minWidth: 140, fontSize: '0.9rem' }}>
                        {d.term}
                      </span>
                      <span style={{ color: '#444', fontSize: '0.9rem', lineHeight: 1.7 }}>{d.def}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Table */}
              {sec.table && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.88rem' }}>
                    <thead>
                      <tr style={{ background: '#F8F9FF' }}>
                        <th style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 700, color: '#1B2A4A', borderBottom: '2px solid #E8ECF8' }}>Purpose</th>
                        <th style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 700, color: '#1B2A4A', borderBottom: '2px solid #E8ECF8' }}>Data used</th>
                        <th style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 700, color: '#1B2A4A', borderBottom: '2px solid #E8ECF8' }}>Lawful basis</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sec.table.map((row, i) => (
                        <tr key={i} style={{ borderBottom: '1px solid #F0F0F0', background: i % 2 === 0 ? '#fff' : '#FAFBFF' }}>
                          <td style={{ padding: '11px 14px', color: '#333', lineHeight: 1.6 }}>{row.purpose}</td>
                          <td style={{ padding: '11px 14px', color: '#555', lineHeight: 1.6 }}>{row.data}</td>
                          <td style={{ padding: '11px 14px', color: '#555', lineHeight: 1.6 }}>{row.basis}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Bullets */}
              {sec.bullets && (
                <ul style={{ paddingLeft: 0, listStyle: 'none', margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {sec.bullets.map((b, i) => (
                    <li key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', color: '#444', lineHeight: 1.7, fontSize: '0.95rem' }}>
                      <span style={{ color: '#1B2A4A', fontWeight: 700, marginTop: 2, flexShrink: 0 }}>→</span>
                      {b}
                    </li>
                  ))}
                </ul>
              )}

              {/* Subsections */}
              {sec.subsections && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: sec.content || sec.bullets ? 16 : 0 }}>
                  {sec.subsections.map((sub) => (
                    <div key={sub.title} style={{ borderLeft: '3px solid #E8ECF8', paddingLeft: 20 }}>
                      <h3 style={{ fontWeight: 700, fontSize: '1rem', color: '#1B2A4A', marginBottom: 10 }}>
                        {sub.title}
                      </h3>
                      {sub.content && (
                        <p style={{ color: '#444', lineHeight: 1.8, marginBottom: sub.bullets || sub.processors ? 12 : 0 }}>
                          {sub.content}
                        </p>
                      )}
                      {sub.bullets && (
                        <ul style={{ paddingLeft: 0, listStyle: 'none', margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {sub.bullets.map((b, i) => (
                            <li key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', color: '#444', lineHeight: 1.7, fontSize: '0.92rem' }}>
                              <span style={{ color: '#1B2A4A', fontWeight: 700, marginTop: 2, flexShrink: 0 }}>→</span>
                              {b}
                            </li>
                          ))}
                        </ul>
                      )}
                      {/* Processor table */}
                      {sub.processors && (
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.87rem' }}>
                            <thead>
                              <tr style={{ background: '#F8F9FF' }}>
                                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#1B2A4A', borderBottom: '2px solid #E8ECF8' }}>Processor</th>
                                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#1B2A4A', borderBottom: '2px solid #E8ECF8' }}>Purpose</th>
                                <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 700, color: '#1B2A4A', borderBottom: '2px solid #E8ECF8' }}>Region</th>
                              </tr>
                            </thead>
                            <tbody>
                              {sub.processors.map((p, i) => (
                                <tr key={i} style={{ borderBottom: '1px solid #F0F0F0', background: i % 2 === 0 ? '#fff' : '#FAFBFF' }}>
                                  <td style={{ padding: '10px 12px', color: '#333', fontWeight: 600 }}>{p.name}</td>
                                  <td style={{ padding: '10px 12px', color: '#555' }}>{p.purpose}</td>
                                  <td style={{ padding: '10px 12px', color: '#555' }}>{p.region}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Footer note */}
              {sec.footer && (
                <p style={{ marginTop: 16, color: '#666', fontSize: '0.9rem', lineHeight: 1.7, fontStyle: 'italic', borderTop: '1px solid #f0f0f0', paddingTop: 14, marginBottom: 0 }}>
                  {sec.footer}
                </p>
              )}
            </div>
          ))}

          {/* Contact card */}
          <div
            style={{
              background: 'linear-gradient(135deg, #1B2A4A, #0D1117)',
              borderRadius: 16,
              padding: '36px',
              textAlign: 'center',
              color: '#fff',
              marginTop: 12,
            }}
          >
            <h3 style={{ fontWeight: 800, fontSize: '1.3rem', marginBottom: 10 }}>
              Privacy questions or data requests?
            </h3>
            <p style={{ opacity: 0.85, marginBottom: 24 }}>
              Contact our Data Protection Officer — we respond within 30 days as required by the DPA.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <a
                href="mailto:dpo@spotfinder.co.ke"
                style={{
                  display: 'inline-block',
                  background: '#00A878',
                  color: '#fff',
                  fontWeight: 700,
                  borderRadius: 10,
                  padding: '12px 24px',
                  textDecoration: 'none',
                  fontSize: '0.92rem',
                }}
              >
                dpo@spotfinder.co.ke
              </a>
              <a
                href="mailto:support@spotfinder.co.ke"
                style={{
                  display: 'inline-block',
                  background: 'rgba(255,255,255,0.1)',
                  color: '#fff',
                  fontWeight: 700,
                  borderRadius: 10,
                  padding: '12px 24px',
                  textDecoration: 'none',
                  fontSize: '0.92rem',
                  border: '1px solid rgba(255,255,255,0.2)',
                }}
              >
                support@spotfinder.co.ke
              </a>
            </div>
            <p style={{ marginTop: 20, opacity: 0.5, fontSize: '0.85rem', marginBottom: 0 }}>
              2026 SpotFinder Kenya Limited. All rights reserved.
            </p>
          </div>

        </div>
      </section>
    </main>
  );
}